import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowLeft } from 'lucide-react';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { sendOtp, verifyOtp, activateAccount } from '../redux/authSlice';
import logo from '../assets/img/logo-TT2.png';

// Activation flow for bulk-uploaded patients. They arrive here from the login
// page after the backend reports `verificationRequired`. Step 1 emails an OTP;
// step 2 verifies it and sets a new password, after which the backend auto-logs
// them in and we route to the patient dashboard.
export default function ActivateAccount() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxiosAuth();

  const [email, setEmail] = useState(location.state?.email || '');
  const [phone, setPhone] = useState(location.state?.phone || '');
  const [step, setStep] = useState(1); // 1 = generate OTP, 2 = verify OTP, 3 = reset password
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);
  const [verifyingPhoneOtp, setVerifyingPhoneOtp] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    timerRef.current = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [resendIn]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneValid = Boolean(phone && String(phone).trim().length >= 7);
  const canContinueToPasswordReset = emailOtpVerified && phoneOtpVerified;

  const handleSendCode = async () => {
    if (!emailValid) {
      toast.warning(t('activate.invalid_email', 'Please enter a valid email address'), { autoClose: 2000 });
      return;
    }
    if (!phoneValid) {
      toast.warning(t('activate.invalid_phone', 'Phone number is required for mobile verification'), { autoClose: 2000 });
      return;
    }
    setSending(true);
    try {
      await Promise.all([
        dispatch(sendOtp({ email, axiosInstance })).unwrap(),
        dispatch(sendOtp({ phone, axiosInstance })).unwrap(),
      ]);
      toast.success(t('activate.code_sent', 'Verification codes have been sent to your email and phone.'), { autoClose: 2500 });
      setStep(2);
      setEmailOtpVerified(false);
      setPhoneOtpVerified(false);
      setResendIn(60);
    } catch (err) {
      toast.warning(typeof err === 'string' ? err : t('activate.send_failed', 'Failed to send verification codes'), { autoClose: 2500 });
    } finally {
      setSending(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtpCode || emailOtpCode.trim().length < 4) {
      toast.warning(t('activate.enter_email_code', 'Enter the email verification code'), { autoClose: 2000 });
      return;
    }
    setVerifyingEmailOtp(true);
    try {
      await dispatch(verifyOtp({ email, otpCode: emailOtpCode.trim(), axiosInstance })).unwrap();
      setEmailOtpVerified(true);
      toast.success(t('activate.email_verified', 'Email OTP verified'), { autoClose: 2000 });
    } catch (err) {
      toast.warning(typeof err === 'string' ? err : t('activate.email_verify_failed', 'Email OTP verification failed'), { autoClose: 2500 });
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!phoneOtpCode || phoneOtpCode.trim().length < 4) {
      toast.warning(t('activate.enter_phone_code', 'Enter the mobile verification code'), { autoClose: 2000 });
      return;
    }
    setVerifyingPhoneOtp(true);
    try {
      await dispatch(verifyOtp({ phone, otpCode: phoneOtpCode.trim(), axiosInstance })).unwrap();
      setPhoneOtpVerified(true);
      toast.success(t('activate.phone_verified', 'Mobile OTP verified'), { autoClose: 2000 });
    } catch (err) {
      toast.warning(typeof err === 'string' ? err : t('activate.phone_verify_failed', 'Mobile OTP verification failed'), { autoClose: 2500 });
    } finally {
      setVerifyingPhoneOtp(false);
    }
  };

  const handleActivate = async () => {
    if (!emailOtpVerified || !phoneOtpVerified) {
      toast.warning(t('activate.verify_both_codes', 'Verify both email and mobile OTPs before continuing'), { autoClose: 2000 });
      return;
    }
    if (newPassword.length < 8) {
      toast.warning(t('activate.password_short', 'Password must be at least 8 characters'), { autoClose: 2000 });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warning(t('activate.password_mismatch', 'Passwords do not match'), { autoClose: 2000 });
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(activateAccount({ email, phone, newPassword, otpVerified: true, axiosInstance })).unwrap();
      toast.success(t('activate.success', 'Account verified. You are now signed in.'), { autoClose: 2500 });
      navigate('/patientDashboard', { replace: true });
    } catch (err) {
      toast.warning(typeof err === 'string' ? err : t('activate.activate_failed', 'Activation failed'), { autoClose: 2500 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]/90">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Therapeu Time" className="h-12 mb-3" />
          <div className="flex items-center gap-2 text-[#3AA8A8]">
            <ShieldCheck className="w-5 h-5" />
            <h1 className="text-xl font-bold">{t('activate.title', 'Verify Your Account')}</h1>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">
            {step === 1
              ? t('activate.subtitle_request', 'Generate OTP codes for email and mobile verification first.')
              : step === 2
                ? t('activate.subtitle_verify', 'Verify both OTP codes before you continue to the password reset form.')
                : t('activate.subtitle_reset', 'Set your new password after both OTP checks are complete.')}
          </p>
        </div>

        {/* Email */}
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.email', 'Email')}</label>
        <div className="relative mb-3">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled
            placeholder="you@example.com"
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
          />
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.phone', 'Mobile Number')}</label>
        <div className="relative mb-4">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled
            placeholder="+1234567890"
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
          />
        </div>

        {step === 1 && (
          <>
            <div className="rounded-xl border border-[#D7EEED] bg-[#F5FCFC] p-4 mb-4 text-sm text-gray-700">
              {t('activate.step1_hint', 'Generate the email and mobile verification codes first. After both are verified, you will move to the password reset form.')}
            </div>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sending || !emailValid || !phoneValid}
              className={`w-full py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${sending || !emailValid || !phoneValid ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {sending ? t('activate.sending', 'Sending…') : t('activate.generate_otp', 'Generate OTP')}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="rounded-xl border border-[#D7EEED] bg-[#F5FCFC] p-4 mb-4 text-sm text-gray-700">
              {t('activate.read_only_notice', 'Verify both OTP codes here. Once both are verified, continue to the password reset form.')}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.email_code', 'Email OTP')}</label>
            <input
              type="text"
              inputMode="numeric"
              value={emailOtpCode}
              onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              className="w-full mb-2 px-3 py-2.5 tracking-[0.4em] text-center border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
            />
            <button
              type="button"
              onClick={handleVerifyEmailOtp}
              disabled={verifyingEmailOtp || emailOtpVerified}
              className={`w-full mb-4 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${verifyingEmailOtp || emailOtpVerified ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {emailOtpVerified ? t('activate.email_verified_done', 'Email OTP verified') : (verifyingEmailOtp ? t('activate.verifying_email', 'Verifying…') : t('activate.verify_email_otp', 'Verify Email OTP'))}
            </button>

            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.phone_code', 'Mobile OTP')}</label>
            <input
              type="text"
              inputMode="numeric"
              value={phoneOtpCode}
              onChange={(e) => setPhoneOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              className="w-full mb-2 px-3 py-2.5 tracking-[0.4em] text-center border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
            />
            <button
              type="button"
              onClick={handleVerifyPhoneOtp}
              disabled={verifyingPhoneOtp || phoneOtpVerified}
              className={`w-full mb-4 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${verifyingPhoneOtp || phoneOtpVerified ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {phoneOtpVerified ? t('activate.phone_verified_done', 'Mobile OTP verified') : (verifyingPhoneOtp ? t('activate.verifying_phone', 'Verifying…') : t('activate.verify_phone_otp', 'Verify Mobile OTP'))}
            </button>

            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={!canContinueToPasswordReset}
              className={`w-full mb-4 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${!canContinueToPasswordReset ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {t('activate.continue_to_reset', 'Continue to password reset')}
            </button>

            <div className="text-center mt-4 text-sm">
              <button
                type="button"
                onClick={handleSendCode}
                disabled={resendIn > 0 || sending}
                className={`text-[#3AA8A8] font-medium ${resendIn > 0 || sending ? 'opacity-50 cursor-not-allowed' : 'hover:underline'}`}
              >
                {resendIn > 0
                  ? t('activate.resend_in', 'Resend code in {{s}}s', { s: resendIn })
                  : t('activate.resend', 'Resend code')}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="rounded-xl border border-[#D7EEED] bg-[#F5FCFC] p-4 mb-4 text-sm text-gray-700">
              {t('activate.reset_hint', 'Both OTP verifications are complete. Enter and confirm your new password below.')}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.new_password', 'New Password')}</label>
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('activate.new_password', 'New Password')}
                className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
              />
              <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.confirm_password', 'Confirm Password')}</label>
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('activate.confirm_password', 'Confirm Password')}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
              />
            </div>

            <button
              type="button"
              onClick={handleActivate}
              disabled={submitting}
              className={`w-full py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${submitting ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {submitting ? t('activate.activating', 'Verifying…') : t('activate.verify_activate', 'Verify & Activate')}
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="mt-6 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4" /> {t('activate.back_to_login', 'Back to login')}
        </button>
      </div>
    </div>
  );
}
